import { addDays, startOfDay, startOfWeek } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DateTimeInput, SelectInput, required } from 'react-admin';

export type DatePreset = {
  id: string;
  name: string;
  filter: () => {
    createdAtGte?: Date | null;
    createdAtLte?: Date | null;
  };
};

export const datePresets: DatePreset[] = [
  {
    id: 'today',
    name: 'Today',
    filter: () => ({
      createdAtGte: startOfDay(new Date()),
    }),
  },
  {
    id: 'yesterday',
    name: 'Yesterday',
    filter: () => {
      const yesterday = startOfDay(addDays(new Date(), -1));

      return {
        createdAtGte: yesterday,
        createdAtLte: addDays(yesterday, 1),
      };
    },
  },
  {
    id: 'currentWeek',
    name: 'Current Week',
    filter: () => ({
      createdAtGte: startOfWeek(new Date(), { weekStartsOn: 1 }),
    }),
  },
  {
    id: 'last7days',
    name: 'Last 7 Days',
    filter: () => ({
      createdAtGte: addDays(new Date(), -7),
    }),
  },
  {
    id: 'lastWeek',
    name: 'Previous Week',
    filter: () => {
      const sevenDaysAgo = addDays(new Date(), -7);
      const lastWeek = startOfWeek(sevenDaysAgo, { weekStartsOn: 0 });

      return {
        createdAtGte: lastWeek,
        createdAtLte: addDays(lastWeek, 7),
      };
    },
  },
  {
    id: 'last30days',
    name: 'Last 30 Days',
    filter: () => ({
      createdAtGte: addDays(new Date(), -30),
    }),
  },
  {
    id: 'custom',
    name: 'Custom',
    filter: () => ({}),
  },
];

/** Presets that imply a bounded range (excludes `custom`). */
export const datePresetsMap = new Map(
  datePresets
    .filter(p => p.id !== 'custom')
    .map(preset => [preset.id, preset]),
);

const hasRangeValue = (v: unknown) =>
  v != null &&
  v !== '' &&
  !(typeof v === 'string' && v.trim() === '');

/**
 * Escape hatch for external callers (e.g. a list-context controller that
 * broadens filters when an admin searches by id/name/email). When a field is
 * registered here, the next "preset -> custom" transition for that field
 * will NOT auto-seed gte/lte from the previous preset. The flag is consumed
 * exactly once.
 */
const fieldsToSkipNextSeed = new Set<string>();

export const suppressNextPresetSeed = (fieldName: string) => {
  fieldsToSkipNextSeed.add(fieldName);
};

const PresetSelectWithSeeding = (
  props: {
    fieldName: string;
    datePresetPinned: boolean;
    /** Declared on the wrapper so RA FilterForm getShownFilters keeps this row mounted. */
    source: string;
    /** Set on the wrapper so FilterFormInput hides the remove control (must not be only on nested SelectInput). */
    alwaysOn?: boolean;
    /** Label of the empty option when not pinned. Pass '' to hide it. */
    datePresetEmptyText?: string;
  },
) => {
  const {
    fieldName,
    datePresetPinned,
    source,
    datePresetEmptyText = '',
  } = props;
  const { setValue, getValues } = useFormContext();
  const preset = useWatch({ name: source });
  const prevPresetRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const gteKey = `${fieldName}Gte` as const;
    const lteKey = `${fieldName}Lte` as const;

    if (
      preset === 'custom' &&
      prevPresetRef.current !== undefined &&
      prevPresetRef.current !== 'custom'
    ) {
      if (fieldsToSkipNextSeed.has(fieldName)) {
        fieldsToSkipNextSeed.delete(fieldName);
        prevPresetRef.current = preset;
        return;
      }

      const gte = getValues(gteKey);
      const lte = getValues(lteKey);

      if (hasRangeValue(gte) && hasRangeValue(lte)) {
        prevPresetRef.current = preset;
        return;
      }

      const prevId = prevPresetRef.current;
      if (prevId && prevId !== 'custom') {
        const p = datePresetsMap.get(prevId);
        if (p) {
          const r = p.filter();
          setValue(gteKey, r.createdAtGte ?? null, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue(lteKey, r.createdAtLte ?? null, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }
    }

    prevPresetRef.current = preset ?? undefined;
  }, [preset, fieldName, setValue, getValues]);

  return (
    <SelectInput
      label="Date Preset"
      source={source}
      alwaysOn={datePresetPinned}
      choices={datePresets}
      {...(datePresetPinned
        ? {
            validate: [required()],
            resettable: false,
          }
        : { emptyText: datePresetEmptyText })}
    />
  );
};

/** RA FilterForm only mounts rows with alwaysOn, displayedFilters, or a set value — range fields stay mounted and hide via sx when collapsed. Top-level `alwaysOn` hides the remove control in FilterFormInput. */
const CollapsedRangeDateTimeInput = (
  props: {
    fieldName: string;
    label?: string;
    suffix: 'Gte' | 'Lte';
    alwaysOn?: boolean;
  },
) => {
  const { fieldName, label, suffix } = props;
  const preset = useWatch({ name: `${fieldName}DatePreset` });
  const source = `${fieldName}${suffix}`;
  const visible = preset === 'custom';
  const prevVisibleRef = useRef(visible);

  useEffect(() => {
    if (prevVisibleRef.current === visible) {
      return;
    }
    prevVisibleRef.current = visible;
  }, [visible, preset, source]);

  const rangeFieldLabel =
    suffix === 'Gte'
      ? label
        ? `${label} (from)`
        : 'From date'
      : label
        ? `${label} (to)`
        : 'To date';

  return (
    <DateTimeInput
      source={source}
      label={rangeFieldLabel}
      alwaysOn
      sx={{ display: visible ? undefined : 'none' }}
    />
  );
};

export type CreateDateFiltersOptions = {
  /**
   * When true: preset stays in the filter form (alwaysOn, no remove row control)
   * and cannot be cleared to an unbounded range (no empty choice, no clear icon).
   */
  datePresetPinned?: boolean;
  /**
   * When true: show After/Before only when preset is `custom`; preset row appears first.
   */
  collapseDateRangeUnlessCustom?: boolean;
  /**
   * Label for the empty option of the (non-pinned) preset select. Defaults to
   * 'All'. Pass '' to render no label for the empty option.
   */
  datePresetEmptyText?: string;
};

export const createDateFilters = (
  fieldName: string,
  label?: string,
  options?: CreateDateFiltersOptions,
) => {
  const datePresetPinned = options?.datePresetPinned ?? false;
  const collapseDateRangeUnlessCustom =
    options?.collapseDateRangeUnlessCustom ?? false;
  const datePresetEmptyText = options?.datePresetEmptyText ?? '';

  const presetSource = `${fieldName}DatePreset`;

  const presetSelect = (
    <PresetSelectWithSeeding
      key={`${fieldName}Preset`}
      alwaysOn={datePresetPinned}
      fieldName={fieldName}
      datePresetPinned={datePresetPinned}
      source={presetSource}
      datePresetEmptyText={datePresetEmptyText}
    />
  );

  if (collapseDateRangeUnlessCustom) {
    return [
      presetSelect,
      <CollapsedRangeDateTimeInput
        key={`${fieldName}Gte`}
        alwaysOn
        fieldName={fieldName}
        label={label}
        suffix="Gte"
      />,
      <CollapsedRangeDateTimeInput
        key={`${fieldName}Lte`}
        alwaysOn
        fieldName={fieldName}
        label={label}
        suffix="Lte"
      />,
    ];
  }

  return [
    <DateTimeInput
      key={`${fieldName}Gte`}
      source={`${fieldName}Gte`}
      label={label ? `${label} (from)` : 'From date'}
    />,
    <DateTimeInput
      key={`${fieldName}Lte`}
      source={`${fieldName}Lte`}
      label={label ? `${label} (to)` : 'To date'}
    />,
    presetSelect,
  ];
};
